"use client";

import type {
  PushPreferences,
  PushSettingsClient,
  PushSettingsSnapshot,
} from "@award-party/shared";

export const PUSH_TOKEN_EVENT = "award-party:push-token";
export const PUSH_PERMISSION_EVENT = "award-party:push-permission";
const PUSH_PERMISSION_MESSAGE = "award-party:request-push-permission";
const OPEN_NOTIFICATION_SETTINGS_MESSAGE =
  "award-party:open-notification-settings";

export type NativePushPermission = "granted" | "denied" | "undetermined";

interface NativePushContext {
  platform?: string;
  expoPushToken?: string;
  pushPermission?: NativePushPermission;
}

interface PushWindow {
  AwardPartyNative?: NativePushContext;
  ReactNativeWebView?: { postMessage: (message: string) => void };
}

function pushWindow(): PushWindow {
  return window as PushWindow;
}

export function urlBase64ToUint8Array(
  base64String: string,
): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const buffer = new ArrayBuffer(raw.length);
  const output = new Uint8Array(buffer);
  for (let index = 0; index < raw.length; index += 1) {
    output[index] = raw.charCodeAt(index);
  }
  return output;
}

export function isNativePushShell(): boolean {
  return Boolean(pushWindow().ReactNativeWebView?.postMessage);
}

export function readInjectedPushToken(): {
  platform: "ios" | "android";
  token: string;
} | null {
  const native = pushWindow().AwardPartyNative;
  if (
    !native ||
    (native.platform !== "ios" && native.platform !== "android") ||
    typeof native.expoPushToken !== "string" ||
    native.expoPushToken.length === 0
  ) {
    return null;
  }
  return { platform: native.platform, token: native.expoPushToken };
}

function requestNativePushPermission(): void {
  pushWindow().ReactNativeWebView?.postMessage(
    JSON.stringify({ type: PUSH_PERMISSION_MESSAGE }),
  );
}

export function readNativePushPermission(): NativePushPermission | null {
  if (typeof window === "undefined") {
    return null;
  }
  const status = pushWindow().AwardPartyNative?.pushPermission;
  if (status === "granted" || status === "denied" || status === "undetermined") {
    return status;
  }
  return null;
}

export function requestOpenNotificationSettings(): void {
  pushWindow().ReactNativeWebView?.postMessage(
    JSON.stringify({ type: OPEN_NOTIFICATION_SETTINGS_MESSAGE }),
  );
}

function waitForNativePushResult(
  timeoutMs = 15000,
): Promise<"granted" | "denied"> {
  if (readInjectedPushToken()) {
    return Promise.resolve("granted");
  }
  if (readNativePushPermission() === "denied") {
    return Promise.resolve("denied");
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(finish, timeoutMs);
    function finish() {
      window.removeEventListener(PUSH_TOKEN_EVENT, onToken);
      window.removeEventListener(PUSH_PERMISSION_EVENT, onPermission);
      resolve(readInjectedPushToken() ? "granted" : "denied");
    }
    function onToken() {
      window.clearTimeout(timer);
      finish();
    }
    function onPermission() {
      if (readNativePushPermission() !== "denied") {
        return;
      }
      window.clearTimeout(timer);
      finish();
    }
    window.addEventListener(PUSH_TOKEN_EVENT, onToken);
    window.addEventListener(PUSH_PERMISSION_EVENT, onPermission);
  });
}

export function waitForNativePushToken(
  timeoutMs = 15000,
): Promise<{ platform: "ios" | "android"; token: string } | null> {
  const existing = readInjectedPushToken();
  if (existing) {
    return Promise.resolve(existing);
  }
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      window.removeEventListener(PUSH_TOKEN_EVENT, onToken);
      resolve(readInjectedPushToken());
    }, timeoutMs);
    function onToken() {
      window.clearTimeout(timer);
      window.removeEventListener(PUSH_TOKEN_EVENT, onToken);
      resolve(readInjectedPushToken());
    }
    window.addEventListener(PUSH_TOKEN_EVENT, onToken);
  });
}

async function currentWebEndpoint(): Promise<string | null> {
  if (!("serviceWorker" in navigator)) {
    return null;
  }
  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();
  return subscription?.endpoint ?? null;
}

async function readJson(response: Response): Promise<unknown> {
  return response.json();
}

export function createBrowserPushClient(locale: string): PushSettingsClient {
  return {
    async load(): Promise<PushSettingsSnapshot> {
      const response = await fetch("/api/push/settings");
      if (!response.ok) {
        throw new Error("loadFailed");
      }
      const body: unknown = await readJson(response);
      if (!body || typeof body !== "object") {
        throw new Error("loadFailed");
      }
      const record = body as {
        preferences?: PushPreferences;
        subscriptions?: PushSettingsSnapshot["subscriptions"];
        vapidPublicKey?: unknown;
      };
      if (!record.preferences || !Array.isArray(record.subscriptions)) {
        throw new Error("loadFailed");
      }
      return {
        preferences: record.preferences,
        subscriptions: record.subscriptions,
        vapidPublicKey:
          typeof record.vapidPublicKey === "string" ? record.vapidPublicKey : null,
      };
    },
    async savePreferences(preferences) {
      const response = await fetch("/api/push/preferences", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(preferences),
      });
      if (!response.ok) {
        throw new Error("saveFailed");
      }
    },
    async currentEndpoint() {
      const native = readInjectedPushToken();
      if (native) {
        return native.token;
      }
      return currentWebEndpoint();
    },
    async enable() {
      if (isNativePushShell()) {
        if (readNativePushPermission() === "denied") {
          return { ok: false, error: "denied" };
        }
        requestNativePushPermission();
        const outcome = await waitForNativePushResult();
        const token = readInjectedPushToken();
        if (outcome !== "granted" || !token) {
          return { ok: false, error: "denied" };
        }
        const response = await fetch("/api/push/subscriptions", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ...token, locale }),
        });
        if (!response.ok) {
          return { ok: false, error: "failed" };
        }
        return { ok: true, endpoint: token.token };
      }

      if (
        !("serviceWorker" in navigator) ||
        !("PushManager" in window) ||
        !("Notification" in window)
      ) {
        return { ok: false, error: "unsupported" };
      }
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        return { ok: false, error: "denied" };
      }
      const settingsResponse = await fetch("/api/push/settings");
      const settings: unknown = await readJson(settingsResponse);
      const vapidPublicKey =
        settings &&
        typeof settings === "object" &&
        typeof (settings as { vapidPublicKey?: unknown }).vapidPublicKey ===
          "string"
          ? (settings as { vapidPublicKey: string }).vapidPublicKey
          : null;
      if (!vapidPublicKey) {
        return { ok: false, error: "notConfigured" };
      }
      const registration = await navigator.serviceWorker.register("/sw.js");
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });
      const json = subscription.toJSON();
      const response = await fetch("/api/push/subscriptions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          platform: "web",
          locale,
          subscription: json,
        }),
      });
      if (!response.ok || !json.endpoint) {
        return { ok: false, error: "failed" };
      }
      return { ok: true, endpoint: json.endpoint };
    },
    async disable(endpoint) {
      if (!isNativePushShell() && "serviceWorker" in navigator) {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription?.endpoint === endpoint) {
          await subscription.unsubscribe();
        }
      }
      const response = await fetch("/api/push/subscriptions", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ endpoint }),
      });
      if (!response.ok) {
        throw new Error("disableFailed");
      }
    },
  };
}

export async function syncExistingPushSubscription(locale: string): Promise<void> {
  if (isNativePushShell()) {
    const token = readInjectedPushToken();
    if (!token) {
      return;
    }
    await fetch("/api/push/subscriptions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ ...token, locale }),
    });
    return;
  }
  if (
    !("Notification" in window) ||
    Notification.permission !== "granted" ||
    !("serviceWorker" in navigator)
  ) {
    return;
  }
  const registration = await navigator.serviceWorker.register("/sw.js");
  const subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    return;
  }
  await fetch("/api/push/subscriptions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      platform: "web",
      locale,
      subscription: subscription.toJSON(),
    }),
  });
}
